import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChalkboard,
  faUsers,
  faUser,
  faUserGroup,
  faFileAlt,
  faSignOutAlt,
  faBars,
  faMasksTheater,
  faXmark,
  faGear,
  faGaugeHigh,
  faTv,
} from "@fortawesome/free-solid-svg-icons";
import {
  getViewAs,
  setViewAs,
  clearViewAs,
  podeUsarVerComo,
} from "../../lib/viewAs";
import professoresService from "../../services/professores.service";
import supabase from "../../config/supabaseConfig.js";
import "./nova-shell.css";

// Moldura nova (dark, tablet-first) das rotas reais migradas.
// Reusa navegação/logout do app. props: userRole (efetivo, já considera o
// modo coringa), realRole (papel real do usuário logado — só ele vê o
// seletor "Ver como"), user, onLogout, children.
const NovaShell = ({ userRole, realRole, user, onLogout, children }) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Esconder/mostrar a sidebar no desktop (libera largura pro conteúdo).
  const [collapsed, setCollapsed] = useState(false);

  // Modo coringa "Ver como" — ferramenta de SUPORTE (homologação), não de
  // operação: só as contas do Rodrigo/Tales veem, mesmo sendo admin real
  // (pedido do Rodrigo no go-live 21/07 — Marco/admins da academia não usam).
  // A lista mora em lib/viewAs.js: quando ela vivia só aqui, ela escondia o
  // botão mas NÃO impedia que um modo já gravado seguisse valendo — foi assim
  // que o Marco ficou preso na visão de professor (fix 27/07).
  const emailAtual = (user?.email || "").toLowerCase();
  const podeVerComo = realRole === "admin" && podeUsarVerComo(emailAtual);
  const viewAs = getViewAs(emailAtual);
  const [verComoAberto, setVerComoAberto] = useState(false);
  const [papelEscolhido, setPapelEscolhido] = useState("professor");
  const [professores, setProfessores] = useState([]);
  const [professorEscolhidoId, setProfessorEscolhidoId] = useState("");
  // profile.role real de cada professor (professores.id == profiles.id):
  // quem é líder (Alex) entra no Ver como já como LÍDER com identidade.
  const [rolesPorProfessor, setRolesPorProfessor] = useState({});

  useEffect(() => {
    if (realRole !== "admin" || !verComoAberto) return;
    let ativo = true;
    (async () => {
      try {
        const data = await professoresService.getAll();
        if (!ativo) return;
        setProfessores(data || []);
        const { data: perfis } = await supabase
          .from("profiles")
          .select("id, role")
          .in(
            "id",
            (data || []).map((p) => p.id)
          );
        if (!ativo) return;
        setRolesPorProfessor(
          Object.fromEntries((perfis || []).map((p) => [p.id, p.role]))
        );
      } catch (err) {
        console.error("Erro ao carregar professores:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [realRole, verComoAberto]);

  const aplicarVerComo = () => {
    if (papelEscolhido === "professor") {
      const prof = professores.find((p) => p.id === professorEscolhidoId);
      if (!prof) return;
      setViewAs(
        {
          // Usa o papel REAL da pessoa (profiles.role), com a identidade dela.
          // Antes o ternário só tratava 'lider' e jogava todo o resto em
          // 'professor' — então escolher o Marco, que é ADMIN e também tem
          // linha em professores, mostrava uma visão de professor que não é a
          // dele. Era o que fazia parecer que o login dele estava errado
          // (fix 27/07). Sem papel conhecido, cai em professor.
          role: rolesPorProfessor[prof.id] || "professor",
          professorId: prof.id,
          professorNome: prof.nome,
        },
        emailAtual
      );
    } else {
      setViewAs({ role: papelEscolhido }, emailAtual);
    }
  };

  const toggleMenu = () => {
    if (window.innerWidth <= 900) setDrawerOpen((v) => !v);
    else setCollapsed((v) => !v);
  };

  // líder/recepção: só Sala + Pré-sala (é quem distribui, não opera o resto).
  const somenteRecepcao = userRole === "lider" || userRole === "recepcao";

  const nav = [
    // Dashboard — visão gerencial, só admin.
    ...(userRole === "admin"
      ? [{ to: "/dashboard", label: "Dashboard", icon: faGaugeHigh }]
      : []),
    // Recepção não opera aula — sem aba Sala (feedback Tales 07/07);
    // o líder dá aula, mantém.
    ...(userRole === "recepcao"
      ? []
      : [{ to: "/sala", label: "Sala", icon: faChalkboard }]),
    ...(userRole === "admin" || somenteRecepcao
      ? [{ to: "/presala", label: "Pré-sala", icon: faUserGroup }]
      : []),
    // Telão — tela de exibição (TV) da recepção; professor não precisa.
    ...(userRole === "admin" || somenteRecepcao
      ? [{ to: "/telao", label: "Telão", icon: faTv }]
      : []),
    // Alunos: todos os papéis. A recepção entrou em 22/07 (pedido Rodrigo:
    // recepção cria/edita alunos; a RLS já foi liberada) — antes era "todo
    // mundo menos recepção".
    { to: "/alunos", label: "Alunos", icon: faUsers },
    // Professores: admin e líder (gestão de equipe — desenho 03/07) e, a
    // partir de 22/07, recepção — que SÓ edita professores (pedido Rodrigo;
    // RLS liberada). Professor comum não vê (não é gestão dele).
    ...(userRole === "admin" ||
    userRole === "lider" ||
    userRole === "recepcao"
      ? [{ to: "/professores", label: "Professores", icon: faUser }]
      : []),
    ...(userRole === "admin"
      ? [{ to: "/relatorios", label: "Relatórios", icon: faFileAlt }]
      : []),
    // Configurações (usuários admin/líder) — só admin.
    ...(userRole === "admin"
      ? [{ to: "/configuracoes", label: "Configurações", icon: faGear }]
      : []),
  ];

  const isActive = (to) => location.pathname.startsWith(to);
  const close = () => setDrawerOpen(false);

  return (
    <div className="nshell">
      <aside
        className={`nshell-sidebar ${drawerOpen ? "open" : ""} ${
          collapsed ? "collapsed" : ""
        }`}
      >
        <div className="nshell-logo">
          <div className="nshell-brand">
            Iron House
            <small>Health &amp; Performance</small>
          </div>
        </div>
        <nav className="nshell-nav">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`nshell-item ${isActive(n.to) ? "active" : ""}`}
              onClick={close}
            >
              <span className="ico">
                <FontAwesomeIcon icon={n.icon} />
              </span>
              {n.label}
            </Link>
          ))}
        </nav>
        <button className="nshell-item nshell-logout" onClick={onLogout}>
          <span className="ico">
            <FontAwesomeIcon icon={faSignOutAlt} />
          </span>
          Sair
        </button>
      </aside>

      <div
        className={`nshell-overlay ${drawerOpen ? "show" : ""}`}
        onClick={close}
      />

      <div className="nshell-main">
        <div className="nshell-topbar">
          <div className="tb-left">
            <button
              className="nshell-hamburger"
              onClick={toggleMenu}
              aria-label="Menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>

          {podeVerComo && (
            <div className="nshell-viewas">
              {viewAs ? (
                <div className="viewas-badge">
                  <FontAwesomeIcon icon={faMasksTheater} />
                  <span>
                    VENDO COMO:{" "}
                    <strong>
                      {(() => {
                        // Rótulo = papel + identidade (quando houver). O papel
                        // vem do profiles.role real da pessoa escolhida, então
                        // "Admin · Marco Fernandes" é resultado legítimo.
                        const rotulo = {
                          admin: "Admin",
                          lider: "Líder",
                          recepcao: "Recepção",
                          professor: "Professor",
                        }[viewAs.role] || viewAs.role;
                        return viewAs.professorNome
                          ? `${rotulo} · ${viewAs.professorNome}`
                          : rotulo;
                      })()}
                    </strong>
                  </span>
                  <button
                    className="viewas-clear"
                    onClick={clearViewAs}
                    aria-label="Sair do modo Ver como"
                    title="Voltar a Admin"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              ) : verComoAberto ? (
                <div className="viewas-picker">
                  <select
                    value={papelEscolhido}
                    onChange={(e) => {
                      setPapelEscolhido(e.target.value);
                      setProfessorEscolhidoId("");
                    }}
                  >
                    <option value="professor">Professor</option>
                    <option value="lider">Líder</option>
                    <option value="recepcao">Recepção</option>
                  </select>
                  {papelEscolhido === "professor" && (
                    <select
                      value={professorEscolhidoId}
                      onChange={(e) => setProfessorEscolhidoId(e.target.value)}
                    >
                      <option value="">Escolha o professor</option>
                      {professores.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome}
                          {rolesPorProfessor[p.id] === "lider"
                            ? " — Líder"
                            : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    className="viewas-apply"
                    onClick={aplicarVerComo}
                    disabled={
                      papelEscolhido === "professor" && !professorEscolhidoId
                    }
                  >
                    Ver
                  </button>
                  <button
                    className="viewas-cancel"
                    onClick={() => setVerComoAberto(false)}
                    aria-label="Cancelar"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              ) : (
                <button
                  className="viewas-toggle"
                  onClick={() => setVerComoAberto(true)}
                >
                  <FontAwesomeIcon icon={faMasksTheater} /> Ver como
                </button>
              )}
            </div>
          )}

          <div className="nshell-user">
            {user && <span className="email">{user.email || "Usuário"}</span>}
            <button className="nshell-item nshell-logout" onClick={onLogout} style={{ width: "auto", padding: "6px 12px" }}>
              <FontAwesomeIcon icon={faSignOutAlt} /> Sair
            </button>
          </div>
        </div>
        <div className="nshell-content">{children}</div>
      </div>
    </div>
  );
};

export default NovaShell;
